import { useState } from 'react';

import dayjs from 'dayjs';

import { useGetPayInfoApi, type GetPayInfoDataType } from '@/api/payment';
import ModalBox from '@/components/common/Modal/ModalBox/ModalBox';
import ModalPortal from '@/components/common/Modal/ModalPortal/ModalPortal';
import PaymentModal from '@/components/common/Modal/PaymentModal/PaymentModal';
import useIsMobile from '@/hooks/useIsMobile';
import { convertPaymentValue } from '@/utils/convertValue';
import { parsePayInfoDate } from '@/utils/dateTime';
import { downloadPdf } from '@/utils/fileUtils';

import * as S from './style';

const displayPayYear = (info: GetPayInfoDataType, fallbackYear: string) => {
	const enroll = parsePayInfoDate(info.pay_info_enrollment_date);
	if (enroll) {
		return enroll.format('YYYY');
	}
	const t = info.pay_info_tran_date;
	if (t != null && typeof t === 'number' && t >= 1900 && t <= 2100) {
		return String(t);
	}
	const tParsed = parsePayInfoDate(t);
	if (tParsed) return tParsed.format('YYYY');
	return fallbackYear;
};

const displayPayDate = (info: GetPayInfoDataType) => {
	if (info.pay_info_enrollment_date && info.pay_info_state === '결제대기') {
		const enroll = parsePayInfoDate(info.pay_info_enrollment_date);
		return enroll ? enroll.format('YYYY-MM-DD') : '-';
	}
	const t = info.pay_info_tran_date;
	if (t != null && typeof t === 'number' && t >= 1900 && t <= 2100) {
		return '-';
	}
	const tParsed = parsePayInfoDate(t);
	if (tParsed) return tParsed.format('YYYY-MM-DD');
	const enrollFallback = parsePayInfoDate(info.pay_info_enrollment_date);
	return enrollFallback ? enrollFallback.format('YYYY-MM-DD') : '-';
};

const isAnnualPending = (info: GetPayInfoDataType) => info.pay_info_state === '결제대기';

const MembershipPayment = () => {
	const isMobile = useIsMobile(900);
	const today = dayjs().format('YYYY');
	const [isOpen, setIsOpen] = useState(false);

	const { data: payInfo } = useGetPayInfoApi();
	const memberType = payInfo?.member_type;

	const onClickPayment = () => {
		setIsOpen(true);
	};

	return (
		<S.Container>
			<S.Title>
				학회 연회비 납부내역
				<S.PaymentButton disabled={!payInfo?.pay_possible} onClick={onClickPayment}>
					납부하기
				</S.PaymentButton>
			</S.Title>
			<>
				{!isMobile && (
					<S.Table>
						<S.TableHead>
							<S.TableRow>
								<S.TableHeadCell>년도</S.TableHeadCell>
								<S.TableHeadCell>연회비</S.TableHeadCell>
								<S.TableHeadCell>지불방법</S.TableHeadCell>
								<S.TableHeadCell>납부일</S.TableHeadCell>
								<S.TableHeadCell>납부</S.TableHeadCell>
							</S.TableRow>
						</S.TableHead>
						<S.TableBody>
							{payInfo &&
								payInfo.pay_info.map((info, index) => (
									<S.TableRow key={index}>
										<S.TableCell>{info.pay_info_pk ? displayPayYear(info, today) : today}</S.TableCell>
										<S.PointCel>
											{info.pay_info_pk
												? `₩
												${
													info.pay_info_amt
														? `${Number(info.pay_info_amt).toLocaleString()} ${info.pay_info_member_type ? `(${info.pay_info_member_type})` : ''}`
														: convertPaymentValue(payInfo.member_type)
												}`
												: '-'}
										</S.PointCel>
										<S.TableCell>{info.pay_info_pay_method ? info.pay_info_pay_method : '-'}</S.TableCell>
										<S.TableCell>{info.pay_info_pk ? displayPayDate(info) : '-'}</S.TableCell>
										<S.TableCell>
											{!info.pay_info_pk ? (
												<S.TableCellDanger>미납</S.TableCellDanger>
											) : isAnnualPending(info) ? (
												<S.TableCellPending>입금대기</S.TableCellPending>
											) : (
												<S.ButtonBox>
													<S.TableCellSuceess>납부완료</S.TableCellSuceess>
													{info.pay_info_pay_method !== '무통장입금' && (
														<S.CompleteStatus type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'receipt')}>
															영수증 확인
														</S.CompleteStatus>
													)}
													<S.CompleteStatus type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'transaction')}>
														거래명세서 확인
													</S.CompleteStatus>
												</S.ButtonBox>
											)}
										</S.TableCell>
									</S.TableRow>
								))}
						</S.TableBody>
					</S.Table>
				)}
				{isMobile &&
					payInfo &&
					payInfo.pay_info.map((info, index) => (
						<S.MobileCard key={index}>
							<S.CardContentBox>
								<S.MenuBox>
									<S.CardMenu>년도</S.CardMenu>
									<div>{info.pay_info_pk ? displayPayYear(info, today) : today}</div>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>연회비</S.CardMenu>
									<S.PointMenu>
										₩
										{info.pay_info_amt
											? `${Number(info.pay_info_amt).toLocaleString()} ${info.pay_info_member_type ? `(${info.pay_info_member_type})` : ''}`
											: convertPaymentValue(payInfo.member_type)}
									</S.PointMenu>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>지불방법</S.CardMenu>
									<div>{info.pay_info_pay_method ? info.pay_info_pay_method : '-'}</div>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>납부일</S.CardMenu>
									<div>{info.pay_info_pk ? displayPayDate(info) : '-'}</div>
								</S.MenuBox>
								<S.MenuBox>
									<S.CardMenu>납부</S.CardMenu>
									<div>
										{!info.pay_info_pk ? (
											<S.TableCellDanger>미납</S.TableCellDanger>
										) : isAnnualPending(info) ? (
											<S.TableCellPending>입금대기</S.TableCellPending>
										) : (
											<S.TableCellSuceess>납부완료</S.TableCellSuceess>
										)}
									</div>
								</S.MenuBox>
							</S.CardContentBox>
							{info.pay_info_pk && !isAnnualPending(info) ? (
								<S.ButtonBox>
									{info.pay_info_pay_method !== '무통장입금' && (
										<S.Button type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'receipt')}>
											영수증 확인
										</S.Button>
									)}
									<S.Button type="button" onClick={() => downloadPdf(info.pay_info_pk ?? '', 'transaction')}>
										거래명세서 확인
									</S.Button>
								</S.ButtonBox>
							) : null}
						</S.MobileCard>
					))}

				{isOpen && payInfo && (
					<ModalPortal>
						<ModalBox onClick={() => setIsOpen(false)}>
							<PaymentModal closeModal={setIsOpen} goodsName="연회비" memberType={memberType} />
						</ModalBox>
					</ModalPortal>
				)}
			</>
		</S.Container>
	);
};
export default MembershipPayment;
